import { useCallback, useRef, useState } from 'react';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useDialog } from '../../shared/hooks/useDialog';
import { useSnackbar } from '../../shared/hooks/useSnackbar';
import { useFriendsData } from '../../../contexts/FriendsDataContext';
import { sendFriendRequest } from '../helpers/friendsApi';

interface AddFriendEntry {
  userId?: string;
  name: string;
}

interface UseAddFriendFromMenuResult {
  handleAddFriend: (entry: AddFriendEntry, onBeforeDialog?: () => void) => Promise<void>;
  /** userId of the user whose request is in flight, or null. */
  sendingUserId: string | null;
}

export function useAddFriendFromMenu(): UseAddFriendFromMenuResult {
  const apiClient = useApiClient();
  const { refreshFriends } = useFriendsData();
  const { showDialog, showAlert } = useDialog();
  const { showSnackbar } = useSnackbar();
  const lockRef = useRef(false);
  const [sendingUserId, setSendingUserId] = useState<string | null>(null);

  const handleAddFriend = useCallback(
    async (entry: AddFriendEntry, onBeforeDialog?: () => void) => {
      if (!entry.userId) return;
      if (lockRef.current) return;
      lockRef.current = true;
      try {
        onBeforeDialog?.();
        const confirmed = await showDialog({
          title: 'Add friend',
          message: `Send a friend request to ${entry.name}?`,
          confirmText: 'Send',
          cancelText: 'Cancel',
        });
        if (!confirmed) return;

        setSendingUserId(entry.userId);
        try {
          const result = await sendFriendRequest(entry.userId, apiClient);
          if (result.success) {
            await refreshFriends();
            showSnackbar('Friend request sent', 'success');
            return;
          }
          await showAlert({
            title: 'Error',
            message: result.message ?? 'Could not send friend request.',
            type: 'danger',
          });
        } catch {
          await showAlert({
            title: 'Error',
            message: 'Could not send friend request. Try again.',
            type: 'danger',
          });
        } finally {
          setSendingUserId(null);
        }
      } finally {
        lockRef.current = false;
      }
    },
    [apiClient, refreshFriends, showDialog, showAlert, showSnackbar],
  );

  return { handleAddFriend, sendingUserId };
}
